import { useEffect } from 'react'
import { classes } from 'typestyle'
import { Link, useParams } from 'react-router-dom'

/* Contents ===================================================================================== */
import { GLOBAL_CONTENTS } from '../../../common/global-contents'

/* Hooks ======================================================================================== */
import useFetch from '../../../hooks/use-fetch'
import useLocal from '../../../hooks/use-local'

/* Core ========================================================================================= */
import { withRouter } from '../../../contexts/with-router'
import { addSnippet } from '../../../core/snippet-manager'
import { flat } from '../../../share/obj-equal'

/* Components =================================================================================== */
import Skeleton from '../../../components/skeleton'
import { Box } from '../../../components/box/box.component'
import { Button } from '../../../components/button'
import { Line } from '../../../components/line'
import { Loader } from '../../../components/loader'
import { Snippet, SnippetHead, SnippetBody } from '../../../modules/layouts/snippet'
import { renderView } from '../../../modules/handlers/render-view'

/* Styles ======================================================================================= */
import { pb8i } from '../../../styles/styleset/padding/pb8i'
import { p12md } from '../../../styles/styleset/padding/p12md'
import { p16 } from '../../../styles/styleset/padding/p16'
import { pt8i } from '../../../styles/styleset/padding/pt8i'
import { mb4 } from '../../../styles/styleset/margin/mb4'
import { mb8 } from '../../../styles/styleset/margin/mb8'

export default addSnippet(
  'GroupInfo',
  withRouter((props: any) => {
    const { groupId } = useParams()

    const [local] = useLocal()
    const apiUrl = `${process.env.REACT_APP_API_URL}/a/snippets/group-info/${groupId}`
    const [req, $req] = useFetch(
      { group: {}, permissions: {}, permissionType: '' },
      { url: apiUrl },
    )

    useEffect(() => {
      if (req.mounted) {
        $req.get()
      }
    }, [req.mounted])

    const group = req.payload.group
    const groupFlatten = flat(group)
    const permissions = req.payload.permissions
    const permissionsFlatten = flat(permissions)
    const permissionType = req.payload.permissionType

    const fields = [
      {
        label: GLOBAL_CONTENTS.name[local],
        name: 'body.name',
      },
      {
        label: GLOBAL_CONTENTS.phone[local],
        name: 'body.phoneNumber',
      },
      {
        label: GLOBAL_CONTENTS.email[local],
        name: 'body.email',
      },
    ]

    const actions = [
      {
        label: GLOBAL_CONTENTS.inviteTeammate[local],
        to: '/a/groups/{{groupId}}/base/team/table/new',
        permission: 'team.w',
      },
      {
        label: GLOBAL_CONTENTS.editGroupProfile[local],
        to: '/a/groups/{{groupId}}/page/group-profile',
        permission: 'group-profile.w',
      },
    ]

    return (
      <Snippet>
        <SnippetHead title={GLOBAL_CONTENTS.groupInformation[local]} />
        <SnippetBody>
          <Box fullWidth row>
            <Loader
              loaded={req.mounted && !req.meta.isLoading}
              timeout={300}
              skeleton={
                <Box>
                  <Skeleton width={80} height={14} className={mb4} />
                  <Skeleton width={'100%'} height={32} className={mb8} />
                  <Skeleton width={80} height={14} className={mb4} />
                  <Skeleton width={'100%'} height={32} className={mb8} />
                  <Skeleton width={80} height={14} className={mb4} />
                  <Skeleton width={'100%'} height={32} className={mb8} />
                </Box>
              }
              component={renderView({
                fields: fields.map((content: any, key: any) => {
                  return { ...content, type: 'TextField', key }
                }),
                readable: false,
                parentName: null,
                flatten: groupFlatten,
                value: group,
              })}
            />
          </Box>
        </SnippetBody>
        <Box baseline="none" rounded>
          <Loader
            loaded={req.mounted && !req.meta.isLoading}
            timeout={300}
            skeleton={
              <Box baseline="none" rounded>
                <Box baseline="none" row>
                  <Line />
                  <Box baseline="none" className={classes(p12md, p16, pb8i, pt8i)}>
                    <Skeleton width="100%" height={32} />
                  </Box>
                </Box>
              </Box>
            }
            component={actions.map((action: any, key: any) => {
              const { label, baseline = 'surface', to, permission } = action || []

              if (permission && !permissionsFlatten[permission] && permissionType !== 'Admin') {
                return
              }

              if (to) {
                return (
                  <Box row key={key} baseline="none">
                    <Line />
                    <Box baseline="none" className={classes(p12md, p16, pb8i, pt8i)}>
                      <Button
                        key={key}
                        as={<Link to={to.replace('{{groupId}}', groupId)} key={key} />}
                        label={label}
                        baseline={baseline}
                        size="md"
                        square
                      />
                    </Box>
                  </Box>
                )
              }

              return (
                <Box row key={key} baseline="none">
                  <Line />
                  <Box className={classes(p12md, p16, pb8i, pt8i)} baseline="none">
                    <Button key={key} label={label} baseline={baseline} size="md" square />
                  </Box>
                </Box>
              )
            })}
          />
        </Box>
      </Snippet>
    )
  }),
)
